import { useCallback, useEffect, useState } from 'react';

/**
 * Persisted collapse state for the right-hand filter rail.
 *
 * Mirrors the left nav's SidebarCollapseContext one for one: a boolean in
 * localStorage under a `<thing>_collapsed` key, default open, written on every
 * toggle so it restores on reload. Kept as a tiny hook rather than joining the
 * nav's context because the two rails are independent choices: collapsing the
 * nav must not fold the filters and vice versa.
 *
 * Every mounted rail shares the value through a storage-style event so two
 * rails on one page (the profile tabs) never disagree.
 */
export const FILTER_RAIL_STORAGE_KEY = 'filter_rail_collapsed';
const CHANGE_EVENT = 'filter-rail-collapse-change';

const read = (): boolean => {
  try {
    return localStorage.getItem(FILTER_RAIL_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const useFilterRailCollapse = () => {
  const [collapsed, setCollapsed] = useState<boolean>(read);

  useEffect(() => {
    const sync = () => setCollapsed(read());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setAndPersist = useCallback((next: boolean) => {
    try {
      localStorage.setItem(FILTER_RAIL_STORAGE_KEY, String(next));
    } catch {
      /* storage unavailable: session-only */
    }
    setCollapsed(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  const toggle = useCallback(() => setAndPersist(!read()), [setAndPersist]);

  return { collapsed, toggle, setCollapsed: setAndPersist };
};

export default useFilterRailCollapse;
